import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import {
  Exercise,
  ExerciseRequest,
  PracticeSession,
  AnswerState,
  ProficiencyLevel,
} from '../types/grammarPractice';
import { getExercise, checkAnswerLocally } from '../api/grammarPracticeApi';
import ExerciseCard from '../components/grammar-practice/ExerciseCard';
import AnswerFeedback from '../components/grammar-practice/AnswerFeedback';
import { FaArrowLeft } from 'react-icons/fa';

const GrammarExercisePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedLanguage } = useLanguage();

  // Get practice parameters from URL
  const level = searchParams.get('level') as ProficiencyLevel | null;
  const topicId = searchParams.get('topicId');

  // Practice session state
  const [currentExercise, setCurrentExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Answer state
  const [answerState, setAnswerState] = useState<AnswerState>({
    submitted: false,
    isCorrect: false,
    userAnswer: '',
  });

  // Session tracking
  const [session, setSession] = useState<PracticeSession>({
    totalAttempts: 0,
    correctAnswers: 0,
    currentStreak: 0,
    bestStreak: 0,
  });

  // Load session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('grammarPracticeSession');
    if (savedSession) {
      try {
        setSession(JSON.parse(savedSession));
      } catch (err) {
        console.error('Failed to load saved session:', err);
      }
    }
  }, []);

  // Save session to localStorage whenever it changes
  useEffect(() => {
    if (session.totalAttempts > 0) {
      localStorage.setItem('grammarPracticeSession', JSON.stringify(session));
    }
  }, [session]);

  // Load first exercise
  useEffect(() => {
    if (selectedLanguage && level) {
      loadNextExercise();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, level, topicId]);

  const loadNextExercise = async () => {
    if (!selectedLanguage || !level) return;

    setLoading(true);
    setError(null);
    setAnswerState({
      submitted: false,
      isCorrect: false,
      userAnswer: '',
    });

    try {
      const languageCode = selectedLanguage.iso639_tag;
      const filters: ExerciseRequest = {
        language: languageCode,
        proficiency_level: level,
        ...(topicId && { grammar_topic_id: parseInt(topicId, 10) }),
      };

      const exercise = await getExercise(filters);
      setCurrentExercise(exercise);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load exercise');
      setCurrentExercise(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = (userAnswer: string) => {
    if (!currentExercise) {
      console.error('handleSubmitAnswer: No current exercise');
      return;
    }

    if (!currentExercise.answer) {
      console.error('handleSubmitAnswer: Exercise missing answer field', currentExercise);
      setError(t('grammar.missingAnswer'));
      return;
    }

    if (!userAnswer || userAnswer.trim() === '') {
      console.error('handleSubmitAnswer: Empty user answer');
      return;
    }

    // Check answer locally (instant feedback)
    const isCorrect = checkAnswerLocally(userAnswer, currentExercise.answer);

    // Update answer state
    setAnswerState({
      submitted: true,
      isCorrect,
      userAnswer,
      correctAnswer: currentExercise.answer,
      explanation: currentExercise.explanation,
    });

    // Update session stats
    setSession((prev) => {
      const newStreak = isCorrect ? prev.currentStreak + 1 : 0;
      return {
        totalAttempts: prev.totalAttempts + 1,
        correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
        currentStreak: newStreak,
        bestStreak: Math.max(prev.bestStreak, newStreak),
      };
    });
  };

  const handleNextQuestion = () => {
    loadNextExercise();
  };

  const handleBack = () => {
    navigate('/grammar');
  };

  const handleOpenDiscussion = () => {
    if (currentExercise) {
      navigate(`/grammar/exercise/${currentExercise.exercise_id}/discussion`, {
        state: { exercise: currentExercise }
      });
    }
  };

  const accuracy = session.totalAttempts > 0
    ? Math.round((session.correctAnswers / session.totalAttempts) * 100)
    : 0;

  if (!selectedLanguage || !level) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 pt-24 pb-24">
        <p className="text-lg text-base-content opacity-60">
          {t('grammar.invalidSession')}
        </p>
        <button onClick={handleBack} className="btn btn-primary mt-4">
          {t('common.backToLevels')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with back button and stats */}
      <div className="fixed top-16 left-0 right-0 bg-base-200 border-b border-base-300 z-30">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={handleBack} className="btn btn-ghost btn-sm gap-2">
            <FaArrowLeft />
            <span className="hidden sm:inline">{t('common.backToLevels')}</span>
          </button>

          <div className="flex items-center gap-2">
            <span className="badge badge-primary">{level}</span>
            {session.totalAttempts > 0 && (
              <>
                <div className="badge badge-sm badge-ghost">
                  {session.totalAttempts} Q
                </div>
                <div className="badge badge-sm badge-success badge-outline">
                  {accuracy}%
                </div>
                <div className="badge badge-sm badge-accent badge-outline">
                  {session.currentStreak} 🔥
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content with padding for header/footer */}
      <div className="flex-1 overflow-y-auto pt-28 pb-20">
        <div className="container mx-auto p-4 max-w-4xl">
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-error my-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-current shrink-0 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{error}</span>
              <button className="btn btn-sm" onClick={loadNextExercise}>
                {t('common.tryAgain')}
              </button>
            </div>
          )}

          {/* Exercise Card */}
          {!loading && !error && currentExercise && (
            <div className={`relative transition-all ${answerState.submitted ? 'pt-24' : 'pt-0'}`}>
              <AnswerFeedback answerState={answerState} onNext={handleNextQuestion} />
              <ExerciseCard
                exercise={currentExercise}
                onSubmit={handleSubmitAnswer}
                disabled={answerState.submitted}
                onOpenDiscussion={handleOpenDiscussion}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrammarExercisePage;
